import React from "react"

import globalStyles from "../../../../App.module.scss"
import styles from "./NginxCertbotMcp.module.scss"
import {Arrow, Box, Diagram, DiagramCaption} from "../../Architecture/shared/Diagram"
import architectureOverview from "./architecture.webp"
import certificateNetworkRequirements from "./certificate-network-requirements.svg"

const REPO_URL = 'https://github.com/julcap/nginx-certbot-mcp'

export default function NginxCertbotMcp() {
    return (
        <div className={globalStyles.article}>
            <h1>nginx-certbot-mcp: Production Infrastructure Access for AI Agents</h1>

            <a className={styles.repoLink} href={REPO_URL} target="_blank" rel="noopener noreferrer">
                View the repo on GitHub &rarr;
            </a>

            <p>An MCP server that lets an AI agent provision Nginx reverse proxies, DNS records and Let's
                Encrypt certificates on real production infrastructure — without ever handing that agent a
                root shell.</p>

            <img className={styles.figure} src={architectureOverview}
                 alt="nginx-certbot-mcp overview: an AI assistant talks to the MCP server over MCP requests, the server performs controlled operations through a privileged wrapper with restricted sudo, which configures Nginx and Certbot, plus DNS operations against AWS Route 53"/>

            <h3>The problem</h3>
            <p>"Put this service behind a reverse proxy with a certificate" is a five-minute task for an
                AI coding agent to describe and a genuinely risky one to let it execute. The obvious way to
                let an agent do infrastructure work is to give it shell access to the box — but nginx
                configs, DNS and certificates all require root, and a shell with root is a shell that can
                touch anything on that machine, not just the things the task actually needs. The safe
                alternative — a human copy-pasting every command the agent proposes — defeats the point of
                delegating the work in the first place. This project is the middle ground: narrowly scoped,
                auditable tools an agent can call directly, each one capable of exactly one category of
                change and nothing else.</p>

            <h3>The stack</h3>
            <div className={globalStyles.styledTable}>
                <table>
                    <thead>
                    <tr>
                        <th>Layer</th>
                        <th>Role</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td><b>MCP server (Node/TypeScript)</b></td>
                        <td>Exposes 21 tools over stdio JSON-RPC — the only surface an agent talks to</td>
                    </tr>
                    <tr>
                        <td><b>nginx-mcp-writesite wrapper</b></td>
                        <td>A purpose-built script that is the sole privileged entry point for touching
                            site configs — nothing else is reachable through it
                        </td>
                    </tr>
                    <tr>
                        <td><b>sudoers rule</b></td>
                        <td>Grants the server's dedicated non-root user passwordless sudo on exactly that
                            wrapper plus <code>nginx -t</code>, a reload, and <code>certbot</code> — nothing
                            broader
                        </td>
                    </tr>
                    <tr>
                        <td><b>Nginx</b></td>
                        <td>Reverse proxy whose server blocks the tools create, archive and restore</td>
                    </tr>
                    <tr>
                        <td><b>Certbot + Let's Encrypt</b></td>
                        <td>Certificate issuance and renewal, via HTTP-01 or DNS-01</td>
                    </tr>
                    <tr>
                        <td><b>Route 53</b></td>
                        <td>DNS records for new sites and the TXT challenges DNS-01 needs for wildcard
                            certs
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>

            <h3>Architecture</h3>
            <Diagram viewBox="0 0 680 260">
                <Box x={10} y={30} width={130} height={40} label="AI agent" sublabel="MCP client"/>
                <Box x={270} y={30} width={150} height={40} label="MCP server" sublabel="21 scoped tools"
                     accent/>
                <Box x={480} y={30} width={190} height={40} label="Narrow sudo"
                     sublabel="wrapper · nginx -t · certbot"/>

                <Box x={270} y={190} width={150} height={40} label="Nginx" sublabel="site configs"/>
                <Box x={480} y={140} width={190} height={40} label="Certbot" sublabel="Let's Encrypt"/>
                <Box x={480} y={190} width={190} height={40} label="Route 53" sublabel="DNS records"/>

                <Arrow x1={140} y1={50} x2={270} y2={50} label="stdio JSON-RPC"/>
                <Arrow x1={420} y1={50} x2={480} y2={50} label="sudo, scoped"/>
                <Arrow x1={555} y1={70} x2={365} y2={190} label="write & reload" dashed/>
                <Arrow x1={555} y1={70} x2={575} y2={140} label="issue / renew" dashed/>
                <Arrow x1={420} y1={50} x2={575} y2={190} label="upsert record" dashed muted/>
            </Diagram>
            <DiagramCaption>The agent never gets a shell. Every privileged action goes through one
                narrowly scoped wrapper and a handful of allow-listed commands — never arbitrary shell
                access to the box.</DiagramCaption>

            <h3>What it can do</h3>
            <ul>
                <li><b>Inspect, read-only.</b> List sites, read a site's config, check certificate expiry,
                    resolve DNS, probe an upstream, tail logs.
                </li>
                <li><b>Manage sites.</b> Create a websocket-capable reverse proxy from a template, disable
                    or delete one (archived, not destroyed), restore from archive, reload nginx safely
                    (<code>nginx -t</code> first, reload only on success).
                </li>
                <li><b>Manage DNS.</b> Upsert or delete Route 53 records, including the TXT records DNS-01
                    validation needs.
                </li>
                <li><b>Manage certificates.</b> Issue via HTTP-01 or DNS-01 wildcard, renew, revoke,
                    delete — defaulting to Let's Encrypt staging so a mistake doesn't burn a production
                    rate limit.
                </li>
            </ul>

            <h3>Certificate issuance: two network paths</h3>
            <p>The two certificate tools prove domain ownership in fundamentally different ways, which
                changes where each one can actually run:</p>
            <img className={styles.figure} src={certificateNetworkRequirements}
                 alt="Diagram comparing HTTP-01 validation, which requires public DNS and port 80 forwarded to the exact Nginx host, with DNS-01 validation, which only needs outbound access to Route 53 and works from any network including a Docker sandbox"/>
            <DiagramCaption>HTTP-01 needs the challenge to land on one exact private IP behind NAT.
                DNS-01 only calls out to Route 53, so it works identically from any network.</DiagramCaption>

            <h3>Decisions I'd defend</h3>
            <ul>
                <li><b>A purpose-built wrapper instead of sudo on generic file tools.</b> An earlier
                    version granted sudo on <code>tee</code>/<code>ln</code>/<code>rm</code> so the server
                    could write into <code>/etc/nginx/</code>. That works, but those commands can touch any
                    root-owned file on the box — not just nginx configs. The wrapper narrows that to one
                    binary that can only write, enable, disable, archive or restore a site config, or tail
                    one of two fixed log files, nothing else.
                </li>
                <li><b>Destructive tools require an explicit <code>confirm:true</code>.</b> Deleting a
                    site, pruning archives or revoking a certificate can't happen as a side effect of an
                    agent being slightly wrong about what it meant to call.
                </li>
                <li><b>A dedicated non-root user, never root.</b> The server's own privileges are exactly
                    what the sudoers rule grants — nothing it does can exceed that, even if the process
                    itself were compromised.
                </li>
                <li><b>DNS-01 for wildcards, HTTP-01 for single domains.</b> See above — DNS-01 trades a
                    dependency on AWS credentials for working the same way on every network.
                </li>
                <li><b>Safe defaults everywhere.</b> Certificate issuance defaults to Let's Encrypt
                    staging, renewal defaults to <code>--dry-run</code>, and the setup script is idempotent
                    — safe to re-run after a username change or an update that adds a new allowed command.
                </li>
            </ul>

            <h3>Tested against real infrastructure</h3>
            <p>Every tool is exercised end to end, not just unit-tested in isolation: a Docker sandbox runs
                systemd as PID 1 so <code>sudo systemctl reload nginx</code> behaves exactly as it would in
                production, and an automated suite drives all 21 tools over the real stdio JSON-RPC
                protocol against that sandbox — or against a real host, exercising the full HTTP-01 flow
                too. Every test run cleans up after itself, including on failure.</p>

            <p>This is the same standard I hold client infrastructure work to: least privilege by default,
                destructive actions gated behind explicit confirmation, and nothing shipped that hasn't
                been exercised against the real thing.</p>
        </div>
    )
}
